import { useState, useEffect } from 'react';

function MultiSelectField(props: {
    id: string;
    label: string;
    extra?: string;
    state?: string;
    disabled?: boolean;
    onChange?: (selectedOptions: string[]) => void;
    defaultValue?: string[];
    value?: string[];
    options: string[];
    required?: boolean;
    columns?: number;
    templateStyle?: {
        backgroundColor?: string;
        borderColor?: string;
        color?: string;
        labelColor?: string;
        accentColor?: string;
    };
}) {
    const {
        label,
        id,
        extra,
        state,
        disabled,
        onChange,
        defaultValue,
        value,
        options,
        required,
        columns = 2,
        templateStyle,
    } = props;

    const [selectedOptions, setSelectedOptions] = useState<string[]>(
        value || defaultValue || []
    );

    useEffect(() => {
        if (value !== undefined) {
            setSelectedOptions(value);
        }
    }, [value]);

    const handleOptionClick = (option: string) => {
        if (disabled) return;
        const newSelection = selectedOptions.includes(option)
            ? selectedOptions.filter((item) => item !== option)
            : [...selectedOptions, option];
        setSelectedOptions(newSelection);
        if (onChange) {
            onChange(newSelection);
        }
    };

    const gridClass =
        columns === 3 ? 'grid-cols-2 sm:grid-cols-3' :
        columns === 4 ? 'grid-cols-2 sm:grid-cols-4' :
        'grid-cols-2';

    return (
        <div className={`${extra}`}>
            <label
                htmlFor={id}
                className="text-sm font-semibold"
                style={{ color: templateStyle?.labelColor || '#374151' }}
            >
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`mt-2.5 grid gap-3 ${gridClass}`}>
                {options.map((option, index) => {
                    const isSelected = selectedOptions.includes(option);
                    const accent = templateStyle?.accentColor;
                    const buttonStyle = isSelected
                        ? {
                            borderColor: accent || undefined,
                            backgroundColor: accent ? `${accent}18` : undefined,
                            color: accent || undefined,
                        }
                        : state === 'error'
                        ? {}
                        : {
                            backgroundColor: templateStyle?.backgroundColor,
                            borderColor: templateStyle?.borderColor
                                ? `${templateStyle.borderColor}80`
                                : undefined,
                            color: templateStyle?.color,
                        };

                    return (
                        <button
                            key={index}
                            type="button"
                            id={index === 0 ? id : undefined}
                            onClick={() => handleOptionClick(option)}
                            disabled={disabled}
                            className={`
                                relative px-3 py-3.5 text-sm font-medium rounded-xl border-2 transition-all duration-200 min-h-[48px]
                                focus:outline-none focus:ring-4 focus:ring-brand-100
                                ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                                ${isSelected
                                    ? 'border-brand-500 bg-brand-50 text-brand-700 shadow-md shadow-brand-500/20'
                                    : state === 'error'
                                    ? 'border-red-200 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-brand-300 hover:bg-brand-50/50'
                                }
                            `}
                            style={buttonStyle}
                        >
                            {isSelected && (
                                <div className="absolute top-2 right-2">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"
                                        style={{ color: accent || undefined }}>
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            )}
                            {option}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default MultiSelectField;
